import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const HERO_IMG = "https://images.pexels.com/photos/23947751/pexels-photo-23947751.jpeg?auto=compress&cs=tinysrgb&w=1920";
const SERVICES = [
  { n: "01", key: "land", title: "Land Aggregation", subtitle: "Growth-corridor parcels, plotted layouts", img: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" },
  { n: "02", key: "construction", title: "Turnkey Construction", subtitle: "Civil + interiors, single-window build", img: "https://images.unsplash.com/photo-1505843513577-22bb7d21e455?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" },
  { n: "03", key: "nri", title: "NRI Property Management", subtitle: "Remote governance & tenant vetting", img: "https://images.pexels.com/photos/6585598/pexels-photo-6585598.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { n: "04", key: "commercial", title: "Commercial Leasing", subtitle: "Grade-A leases & sale-leasebacks", img: "https://images.pexels.com/photos/26838218/pexels-photo-26838218.jpeg?auto=compress&cs=tinysrgb&w=1200" },
  { n: "05", key: "distressed", title: "Distressed Flips", subtitle: "SARFAESI auctions, rehab plays", img: "https://images.unsplash.com/photo-1617546127542-eb0dd8bdd52f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" },
  { n: "06", key: "jv", title: "Joint Venture Development", subtitle: "Landowner JVs, DM mandates", img: "https://images.pexels.com/photos/8135492/pexels-photo-8135492.jpeg?auto=compress&cs=tinysrgb&w=1200" },
];

const CHAPTERS = [
  { n: "I", title: "We source what the market can't see.", body: "Off-market land banks, pre-NCLT auctions, and pre-leased commercial floors surfaced through a 400-broker private network across five metros." },
  { n: "II", title: "We build to institutional spec.", body: "Vertically integrated civil + interiors with published SLA-backed quality tiers. Every project ships with title indemnity and warranty escrow." },
  { n: "III", title: "We manage as if we owned it.", body: "White-glove property governance for NRIs and family offices — from tenant vetting and rent repatriation to statutory filings." },
  { n: "IV", title: "We exit with discipline.", body: "Structured JVs, sale-leasebacks and distress flips underwritten to institutional IRR bands, not retail heuristics." },
];

export default function Home() {
  const [tab, setTab] = useState("residential");
  const [filters, setFilters] = useState({ location: "All Locations", asset: "Any", price: "Any", clearTitle: true });
  const nav = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (tab) params.set("category", tab);
    if (filters.location !== "All Locations") params.set("location", filters.location);
    if (filters.asset !== "Any") params.set("asset_type", filters.asset);
    if (filters.price !== "Any") params.set("price_range", filters.price);
    if (filters.clearTitle) params.set("clear_title", "true");
    nav(`/properties?${params.toString()}`);
  };

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100vh] pt-[72px] overflow-hidden">
        <motion.div className="absolute inset-0 -z-10" style={{ y: heroY, scale: heroScale }}>
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A2342]/40 via-[#0A2342]/20 to-[#0A2342]" />
        </motion.div>

        <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-16 md:pt-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }} className="flex items-center gap-4 mb-8">
            <span className="w-8 hairline-bronze" />
            <span className="accent-dot" />
            <span className="eyebrow">Est. 2026 · Bengaluru, India</span>
          </motion.div>

          <h1 className="display-1 max-w-[1200px]">
            <MaskedLines lines={["Engineering value", "across the real", "estate lifecycle."]} testid="hero-headline" />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 max-w-xl text-[15px] leading-[1.7] text-[#EDE6D3]/75"
          >
            From land aggregation and turnkey build to institutional asset management, JV development and pre-NCLT distress flips — one desk, one mandate.
          </motion.p>

          {/* Omnibox */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-14 md:mt-20 bg-[#0A2342]/90 backdrop-blur-xl border border-[#EDE6D3]/15 max-w-[1100px]"
            data-testid="omnibox"
          >
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="w-full flex bg-transparent rounded-none border-b border-[#EDE6D3]/10 p-0 h-auto">
                {[
                  { v: "land", l: "Buy Land" },
                  { v: "residential", l: "Residential & Villas" },
                  { v: "commercial", l: "Commercial & Leasing" },
                  { v: "distressed", l: "Distressed Deals" },
                ].map((t) => (
                  <TabsTrigger
                    key={t.v}
                    value={t.v}
                    data-testid={`omnibox-tab-${t.v}`}
                    className="flex-1 rounded-none py-4 text-[11px] tracking-[2px] uppercase data-[state=active]:bg-[#EDE6D3] data-[state=active]:text-[#0A2342] data-[state=active]:shadow-none"
                  >
                    {t.l}
                  </TabsTrigger>
                ))}
              </TabsList>

              {["land", "residential", "commercial", "distressed"].map((v) => (
                <TabsContent key={v} value={v} className="p-6 md:p-8 mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8">
                    <div><div className="bb-label">Location</div>
                      <select className="bb-input" data-testid={`omni-${v}-location`} value={filters.location} onChange={(e) => setFilters((f) => ({ ...f, location: e.target.value }))}>
                        <option>All Locations</option><option>Bengaluru</option><option>Whitefield</option><option>Devanahalli</option><option>Yelahanka</option><option>Peenya</option>
                      </select>
                    </div>
                    <div><div className="bb-label">Asset Type</div>
                      <select className="bb-input" data-testid={`omni-${v}-asset`} value={filters.asset} onChange={(e) => setFilters((f) => ({ ...f, asset: e.target.value }))}>
                        <option>Any</option><option>Villa</option><option>Commercial</option><option>Land</option><option>Distressed</option>
                      </select>
                    </div>
                    <div><div className="bb-label">Price Range</div>
                      <select className="bb-input" data-testid={`omni-${v}-price`} value={filters.price} onChange={(e) => setFilters((f) => ({ ...f, price: e.target.value }))}>
                        <option>Any</option><option>Under ₹2 Cr</option><option>₹2 - ₹8 Cr</option><option>₹8 - ₹20 Cr</option><option>₹20 Cr +</option>
                      </select>
                    </div>
                    <div className="flex items-end justify-between">
                      <label className="flex items-center gap-3 cursor-pointer" data-testid={`omni-${v}-cleartitle`}>
                        <span className="relative inline-block w-9 h-5">
                          <input type="checkbox" className="peer sr-only" checked={filters.clearTitle} onChange={(e) => setFilters((f) => ({ ...f, clearTitle: e.target.checked }))} />
                          <span className="absolute inset-0 bg-[#EDE6D3]/15 peer-checked:bg-[#D4AF37] transition-colors" />
                          <span className="absolute top-[2px] left-[2px] w-4 h-4 bg-[#0A2342] peer-checked:translate-x-4 transition-transform" />
                        </span>
                        <span className="text-[11px] tracking-[2px] uppercase">Clear Title</span>
                      </label>
                      <button className="btn-primary" data-testid={`omni-${v}-search`} onClick={handleSearch}>Search <ArrowRight size={14} /></button>
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </motion.div>

          <div className="mt-16 md:mt-24 max-w-3xl">
            <Reveal>
              <p className="text-[13px] tracking-[3px] uppercase text-[#EDE6D3]/50">Founded 2026 · Headquartered in Bengaluru</p>
              <p className="mt-4 font-serif text-[26px] md:text-[34px] leading-[1.35] text-[#EDE6D3]/85 max-w-2xl">
                A new-generation real-estate desk, purpose-built for a single city and its most demanding owners.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#EDE6D3]/10 py-10 mt-24 overflow-hidden bg-[#0A2342]" data-testid="marquee-signals">
        <Marquee gradient={false} speed={30}>
          {["RERA Karnataka Registered", "SEBI Cat-II AIF Advisor", "Sotheby's Referral Partner", "Colliers Alliance", "Confederation of Real Estate", "IGBC Green Certified Builds", "SARFAESI Cell Access"].map((s, i) => (
            <div key={i} className="flex items-center gap-8 px-10">
              <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />
              <span className="text-[12px] tracking-[3px] uppercase text-[#EDE6D3]/70">{s}</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* SERVICE STACK */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-16">
          <div className="md:col-span-4">
            <div className="chapter-num">CHAPTER · 01 / 03</div>
            <h2 className="mt-4 font-serif text-[44px] md:text-[64px] leading-[0.95] tracking-tight">The full-service stack.</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-[15px] leading-[1.8] text-[#EDE6D3]/75">
              Six operating verticals, one balance sheet. We underwrite, build, lease and exit — so capital compounds inside a single mandate instead of leaking through six vendors.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="services-grid">
          {SERVICES.map((s, i) => {
            const span = i === 0 || i === 3 ? "md:col-span-7" : (i === 1 || i === 4) ? "md:col-span-5" : "md:col-span-6";
            return (
              <Reveal key={s.key} delay={i * 0.05} className={`${span}`}>
                <Link
                  to={s.key === "nri" ? "/nri" : s.key === "distressed" || s.key === "jv" ? "/portal" : "/services"}
                  className="group block card-sand relative overflow-hidden"
                  data-testid={`service-card-${s.key}`}
                >
                  <div className="relative a-16-10 overflow-hidden">
                    <img src={s.img} alt={s.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#EDE6D3]/20 via-transparent to-transparent" />
                    <div className="absolute top-5 left-5 chapter-num text-[#0A2342]/90">— {s.n}</div>
                  </div>
                  <div className="p-6 md:p-8 flex items-start justify-between gap-6">
                    <div>
                      <h3 className="font-serif text-[26px] md:text-[32px] leading-tight">{s.title}</h3>
                      <p className="mt-2 text-[13px] text-[#EDE6D3]/65">{s.subtitle}</p>
                    </div>
                    <div className="mt-1 border border-[#EDE6D3]/30 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37] transition-colors p-3">
                      <ArrowUpRight size={18} />
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-40" data-testid="manifesto">
        <div className="flex items-baseline justify-between mb-6 gap-6 flex-wrap">
          <div className="eyebrow">Manifesto · Four Principles</div>
          <div className="chapter-num">CHAPTER · 02 / 03</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08} className="md:col-span-6 border-t border-[#EDE6D3]/25 pt-8">
              <div className="chapter-num mb-6">§ {c.n}</div>
              <h3 className="font-serif text-[32px] md:text-[42px] leading-[1.05] tracking-tight max-w-md">{c.title}</h3>
              <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70 max-w-md">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA CHAPTER */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-40 pb-8">
        <Reveal>
          <div className="bg-[#EDE6D3] text-[#0A2342] p-10 md:p-20 relative grain overflow-hidden">
            <div className="chapter-num text-[#D4AF37]">CHAPTER · 03 / 03</div>
            <h2 className="mt-6 font-serif text-[44px] md:text-[80px] leading-[0.95] tracking-tight max-w-4xl">
              A private conversation, not a brochure.
            </h2>
            <p className="mt-8 max-w-xl text-[15px] leading-[1.8] text-[#0A2342]/70">
              Every mandate we accept begins with a two-hour discovery call. If our thesis and yours don't align, we say so — before the paperwork.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/portal" className="btn-bronze" data-testid="cta-request-access">Request Access <ArrowUpRight size={14} /></Link>
              <Link to="/services" className="text-[12px] tracking-[2px] uppercase text-[#0A2342] link-underline">Explore calculators →</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
